import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import { useToast } from "@/shared/components/ui/use-toast";
import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { ArrowLeft, Save, User, Shield, Eye, EyeOff, Plus } from "lucide-react";

const FORM_CSS = `
  .sf-wrap { font-family:'Outfit',system-ui,sans-serif; }
  .sf-card { background:linear-gradient(135deg,#0d1f35,#091525); border:1px solid #1e3a5f; border-radius:16px; overflow:hidden; transition:border-color .2s; margin-bottom:18px; }
  .sf-card:hover { border-color:rgba(34,211,238,0.2); }
  .sf-card:last-of-type { margin-bottom:0; }
  .sf-card-hd { padding:18px 22px; border-bottom:1px solid #1e3a5f; background:rgba(34,211,238,0.03); }
  .sf-card-title { font-size:14px; font-weight:700; color:#eff6ff; display:flex; align-items:center; gap:8px; }
  .sf-card-title::before { content:''; width:3px; height:15px; background:linear-gradient(180deg,#22d3ee,#3b82f6); border-radius:2px; flex-shrink:0; }
  .sf-card-desc { font-size:12px; color:#4d6e90; margin-top:3px; padding-left:11px; }
  .sf-card-bd { padding:20px 22px; }
  .sf-lbl { font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; display:block; margin-bottom:6px; }
  .sf-inp { background:#060f1e; border:1px solid #1e3a5f; color:#eff6ff; border-radius:10px; padding:10px 14px; font-size:14px; width:100%; outline:none; transition:all .2s; font-family:inherit; }
  .sf-inp:focus { border-color:#22d3ee; box-shadow:0 0 0 3px rgba(34,211,238,0.1); }
  .sf-inp::placeholder { color:#2d4a6a; }
  .sf-inp:-webkit-autofill,.sf-inp:-webkit-autofill:focus { -webkit-box-shadow:0 0 0 1000px #060f1e inset!important; -webkit-text-fill-color:#eff6ff!important; }
  .sf-eye-wrap { position:relative; }
  .sf-eye-wrap .sf-inp { padding-right:42px; }
  .sf-eye { position:absolute; right:0; top:0; height:100%; width:40px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; color:#4d6e90; cursor:pointer; transition:color .2s; }
  .sf-eye:hover { color:#22d3ee; }
  .sf-g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media(max-width:640px){ .sf-g2 { grid-template-columns:1fr; } }
  .sf-field { margin-bottom:16px; }
  .sf-field:last-child { margin-bottom:0; }
  .sf-role-item { display:flex; align-items:flex-start; gap:12px; padding:14px 16px; border:1px solid #1e3a5f; border-radius:12px; cursor:pointer; transition:all .2s; background:#060f1e; margin-bottom:8px; }
  .sf-role-item:last-child { margin-bottom:0; }
  .sf-role-item:hover { border-color:rgba(34,211,238,0.28); background:rgba(34,211,238,0.05); }
  .sf-role-item.assigned { border-color:rgba(34,211,238,0.38); background:rgba(34,211,238,0.09); }
  .sf-role-name { font-size:14px; font-weight:600; color:#e2e8f0; display:flex; align-items:center; gap:6px; }
  .sf-role-desc { font-size:12px; color:#4d6e90; margin-top:3px; }
  .sf-perm-tag { font-size:11px; background:rgba(34,211,238,0.08); color:#64748b; padding:2px 7px; border-radius:4px; border:1px solid rgba(34,211,238,0.15); }
  .sf-cb { width:18px; height:18px; border-radius:5px; border:2px solid #2d4a6a; background:transparent; flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .15s; cursor:pointer; margin-top:2px; }
  .sf-cb.on { background:#22d3ee; border-color:#22d3ee; }
  .sf-empty { text-align:center; padding:40px 20px; color:#2d4a6a; border:2px dashed #1e3a5f; border-radius:12px; }
  .sf-btn-primary { background:linear-gradient(135deg,#22d3ee,#0891b2); color:#04111f; border:none; border-radius:10px; padding:10px 22px; font-size:14px; font-weight:700; display:flex; align-items:center; gap:8px; cursor:pointer; transition:all .2s; box-shadow:0 4px 14px rgba(34,211,238,0.28); font-family:inherit; }
  .sf-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(34,211,238,0.4); }
  .sf-btn-primary:disabled { opacity:.45; cursor:not-allowed; transform:none; }
  .sf-btn-outline { background:transparent; color:#64748b; border:1px solid #1e3a5f; border-radius:10px; padding:10px 20px; font-size:14px; font-weight:500; cursor:pointer; transition:all .2s; text-decoration:none; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
  .sf-btn-outline:hover { border-color:rgba(34,211,238,0.3); color:#93b4d4; background:rgba(34,211,238,0.05); }
  .sf-btn-ghost { background:transparent; color:#4d6e90; border:1px solid #1e3a5f; border-radius:10px; padding:9px; cursor:pointer; transition:all .2s; display:flex; align-items:center; justify-content:center; }
  .sf-btn-ghost:hover { border-color:rgba(34,211,238,0.35); color:#22d3ee; background:rgba(34,211,238,0.08); }
  .sf-select-dark { background:#060f1e!important; border:1px solid #1e3a5f!important; color:#eff6ff!important; border-radius:10px!important; height:40px!important; }
  [data-radix-select-content] { background:#0d1f35!important; border:1px solid #1e3a5f!important; border-radius:12px!important; box-shadow:0 16px 48px rgba(0,0,0,0.6)!important; }
  [data-radix-select-item] { color:#94a3b8!important; font-size:13px!important; }
  [data-radix-select-item]:focus,[data-radix-select-item][data-highlighted] { background:rgba(34,211,238,0.08)!important; color:#22d3ee!important; }
  @keyframes sf-spin { to { transform:rotate(360deg); } }
  .sf-spinner { width:28px; height:28px; border:2px solid transparent; border-top-color:#22d3ee; border-radius:50%; animation:sf-spin 1s linear infinite; }
  .sf-inp.error-border { border-color: #ef4444 !important; }
  .sf-card.error-border { border-color: #ef4444 !important; }
`;

export default function AdminStaffForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const staffId = urlParams.get("id");
  const isEdit = !!staffId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const xlPerm = k => { const tr = t(`permission.${k}`); return tr !== `permission.${k}` ? tr : k.split('.').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '); };
  const xlRole = n => { const tr = t(`role.${n}`); return tr !== `role.${n}` ? tr : n; };
  const xlDesc = d => { if (!d) return ''; const tr = t(`roleDesc.${d}`); return tr !== `roleDesc.${d}` ? tr : d; };

  const { toast } = useToast();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState({
    email: "", password: "", name: "", status: "ACTIVE",
    parentType: "ADMIN", parentId: "", roleIds: [],
  });

  useEffect(() => {
    if (isEdit) { setLoadingUser(false); return; }
    const load = async () => {
      try {
        const u = await auth.me();
        const uid = u?.userId || u?.id;
        if (uid) { setCurrentUserId(uid); setForm(p => ({ ...p, parentId: uid })); }
        else {
          const ud = auth.getUserData();
          if (ud?.userId) { setCurrentUserId(ud.userId); setForm(p => ({ ...p, parentId: ud.userId })); }
        }
      } catch {
        const ud = auth.getUserData();
        if (ud?.userId) { setCurrentUserId(ud.userId); setForm(p => ({ ...p, parentId: ud.userId })); }
      } finally { setLoadingUser(false); }
    };
    load();
  }, [isEdit]);

  const { data: existing } = useQuery({ queryKey: ["staff", staffId], queryFn: () => adminService.getStaffUserById(staffId), enabled: isEdit });
  const { data: roles = [] } = useQuery({ queryKey: ["available-roles"], queryFn: () => adminService.getAvailableRoles() });

  useEffect(() => {
    if (existing) setForm(p => ({ ...p, name: existing.name, email: existing.email, password: "", status: existing.status, parentType: existing.parentType, parentId: existing.parentId, roleIds: existing.roles?.map(r => r.id) || [] }));
  }, [existing]);

  const createMut = useMutation({ mutationFn: d => adminService.createStaffUser(d), onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); toast({ title: "Success", description: "Staff member created successfully" }); } });
  const updateMut = useMutation({ mutationFn: ({ id, data }) => adminService.updateStaffUser(id, data), onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); queryClient.invalidateQueries(["staff", staffId]); toast({ title: "Success", description: "Staff member updated successfully" }); } });
  const assignMut = useMutation({ mutationFn: i => adminService.assignRoleToStaff(i), onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); queryClient.invalidateQueries(["staff", staffId]); } });
  const removeMut = useMutation({ mutationFn: i => adminService.removeRoleFromStaff(i), onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); queryClient.invalidateQueries(["staff", staffId]); } });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleRole = (roleId) => {
    // Clear the error as soon as they interact
    if (formErrors.roles) setFormErrors(p => ({ ...p, roles: false }));
    setForm(p => ({ ...p, roleIds: p.roleIds?.includes(roleId) ? p.roleIds.filter(id => id !== roleId) : [...(p.roleIds || []), roleId] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!form.name) newErrors.name = true;
    if (!form.email) newErrors.email = true;
    if (!isEdit && !form.password) newErrors.password = true;
    else if (!isEdit && form.password.length < 6) newErrors.passwordLen = true;

    // Validate roles
    const hasRoles = form.roleIds?.length > 0;
    if (!hasRoles) newErrors.roles = true;

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (newErrors.roles && Object.keys(newErrors).length === 1) {
        toast({ variant: "destructive", title: "Error", description: t("pleaseAssignAtLeastOneRole") });
      } else {
        toast({ variant: "destructive", title: "Error", description: t("pleaseFillAllRequiredFields") });
      }
      return;
    }

    if (!isEdit && !form.parentId) { toast({ variant: "destructive", title: "Error", description: t("unableToGetCurrentUserId") }); return; }

    setSaving(true);
    try {
      if (isEdit) {
        const d = { name: form.name, status: form.status };
        if (form.password) d.password = form.password;
        await updateMut.mutateAsync({ id: staffId, data: d });

        const oldRoles = existing?.roles?.map(r => r.id) || [];
        const newRoles = form.roleIds || [];
        const toAdd = newRoles.filter(r => !oldRoles.includes(r));
        const toRemove = oldRoles.filter(r => !newRoles.includes(r));

        if (toAdd.length > 0 || toRemove.length > 0) {
          for (const roleId of toAdd) {
            await assignMut.mutateAsync({ staffUserId: staffId, roleId });
          }
          for (const roleId of toRemove) {
            await removeMut.mutateAsync({ staffUserId: staffId, roleId });
          }
          queryClient.invalidateQueries(["staff", staffId]);
        }
      } else {
        const input = { email: form.email, password: form.password, name: form.name, parentType: form.parentType, parentId: form.parentId };
        if (form.roleIds?.length) input.roleIds = form.roleIds;
        await createMut.mutateAsync(input);
      }
      navigate(createPageUrl("AdminStaff"));
    } catch (e) {
      toast({ variant: "destructive", title: "Error", description: e.message || "An error occurred" });
      console.error(e);
    }
    finally { setSaving(false); }
  };

  if (loadingUser && !isEdit) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 360 }}>
        <div style={{ textAlign: "center" }}>
          <div className="sf-spinner" style={{ margin: "0 auto 14px" }} />
          <p style={{ color: "#4d6e90", fontSize: 14 }}>{t("loadingUserInformation")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-wrap" style={{ maxWidth: 860, paddingBottom: 32 }}>
      <style>{FORM_CSS}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 26 }}>
        <Link to={createPageUrl("AdminStaff")}>
          <button className="sf-btn-ghost"><ArrowLeft size={18} /></button>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,rgba(16,185,129,0.18),rgba(6,182,212,0.15))", border: "1px solid rgba(16,185,129,0.28)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Shield size={20} style={{ color: "#10b981" }} />
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#eff6ff", margin: 0, letterSpacing: "-0.02em" }}>{isEdit ? t("editStaffMember") : t("createStaffMember")}</h1>
            <p style={{ fontSize: 13, color: "#4d6e90", margin: 0 }}>{isEdit ? t("updateStaffMemberDetails") : t("addNewStaffMemberToOrganization")}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 0 }}>

        {/* General Info Card */}
        <div className="sf-card">
          <div className="sf-card-hd">
            <div className="sf-card-title"><User size={15} />{t("generalInformation")}</div>
            <div className="sf-card-desc">{t("basicDetailsForStaffMember")}</div>
          </div>
          <div className="sf-card-bd">
            <div className="sf-g2 sf-field">
              <div>
                <label className="sf-lbl">{t("fullName")} *</label>
                <input className={`sf-inp ${formErrors.name ? 'error-border' : ''}`} value={form.name} onChange={e => { set("name", e.target.value); setFormErrors(p => ({ ...p, name: false })); }} autoComplete="off" />
              </div>
              <div>
                <label className="sf-lbl">{t("emailAddress")} *</label>
                <input className={`sf-inp ${formErrors.email ? 'error-border' : ''}`} type="email" value={form.email} onChange={e => { set("email", e.target.value); setFormErrors(p => ({ ...p, email: false })); }} autoComplete="off" disabled={isEdit} style={isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}} />
              </div>
            </div>
            <div className="sf-field">
              <label className="sf-lbl">{isEdit ? t("newPasswordLeaveBlank") : `${t("password")} *`}</label>
              <div className="sf-eye-wrap">
                <input className={`sf-inp ${(formErrors.password || formErrors.passwordLen) ? 'error-border' : ''}`} type={showPwd ? "text" : "password"} value={form.password} onChange={e => { set("password", e.target.value); setFormErrors(p => ({ ...p, password: false, passwordLen: false })); }} autoComplete="new-password" />
                <button type="button" className="sf-eye" onClick={() => setShowPwd(!showPwd)}>{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
            </div>
            {isEdit && (
              <div className="sf-field" style={{ marginBottom: 0 }}>
                <label className="sf-lbl">{t("status")}</label>
                <Select value={form.status} onValueChange={v => set("status", v)}>
                  <SelectTrigger className="sf-select-dark"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                    <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                    <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Role Assignment Card */}
        <div className={`sf-card ${formErrors.roles ? 'error-border' : ''}`}>
          <div className="sf-card-hd">
            <div className="sf-card-title"><Shield size={15} />{t("roleAssignment")}</div>
            <div className="sf-card-desc">{t("assignRolesToControlAccess")}</div>
          </div>
          <div className="sf-card-bd">
            {roles.length === 0 ? (
              <div className="sf-empty">
                <Shield size={36} style={{ margin: "0 auto 10px", opacity: 0.25 }} />
                <p style={{ fontSize: 14, marginBottom: 14 }}>{t("noRolesAvailableCreateFirst")}</p>
                <Link to={createPageUrl("AdminRoleForm")}>
                  <button type="button" className="sf-btn-outline"><Plus size={14} />{t("createRole")}</button>
                </Link>
              </div>
            ) : (
              roles.map(role => {
                const isAssigned = form.roleIds?.includes(role.id);
                return (
                  <div key={role.id} className={`sf-role-item${isAssigned ? " assigned" : ""}`} onClick={() => toggleRole(role.id)}>
                    <div className={`sf-cb${isAssigned ? " on" : ""}`}>
                      {isAssigned && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="sf-role-name">
                        <Shield size={13} style={{ color: "#22d3ee" }} />
                        {xlRole(role.name)}
                        {isAssigned && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(34,211,238,0.12)", color: "#22d3ee", border: "1px solid rgba(34,211,238,0.25)" }}>Assigned</span>}
                      </div>
                      {role.description && <p className="sf-role-desc">{xlDesc(role.description)}</p>}
                      {role.permissions?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                          {role.permissions.slice(0, 5).map((perm, i) => (
                            <span key={i} className="sf-perm-tag">{xlPerm(perm)}</span>
                          ))}
                          {role.permissions.length > 5 && (
                            <span style={{ fontSize: 11, color: "#2d4a6a", display: "flex", alignItems: "center" }}>+{role.permissions.length - 5} {t("morePermissions")}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 20, borderTop: "1px solid #1e3a5f" }}>
          <Link to={createPageUrl("AdminStaff")}>
            <button type="button" className="sf-btn-outline">{t("cancel")}</button>
          </Link>
          <button type="submit" disabled={saving} className="sf-btn-primary">
            {saving
              ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}><Save size={15} /></motion.div>{t("saving")}</>
              : <><Save size={15} />{isEdit ? t("updateStaffMember") : t("createStaffMember")}</>}
          </button>
        </div>

      </form>
    </div>
  );
}